import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Activity, AlertTriangle, Pause, Play } from "lucide-react";
import { formatNGN } from "../lib/format";

/**
 * Live alerts ticker — simulates real-time alert stream by rotating through
 * the seeded alerts. New alerts slide in from the right.
 */
export function AlertTicker({ alerts = [] }) {
  const [tick, setTick] = useState(0);
  const [paused, setPaused] = useState(false);
  const [feed, setFeed] = useState([]);
  const idxRef = useRef(0);

  useEffect(() => {
    if (!alerts.length) return;
    // Seed feed with first 5
    setFeed(alerts.slice(0, 5).map((a, i) => ({ ...a, _key: `init-${i}`, _age: i })));
    idxRef.current = 5 % alerts.length;
  }, [alerts]);

  useEffect(() => {
    if (paused || !alerts.length) return;
    const id = setInterval(() => {
      setTick((t) => t + 1);
      setFeed((prev) => {
        const next = alerts[idxRef.current % alerts.length];
        idxRef.current = (idxRef.current + 1) % alerts.length;
        const aged = prev.map((p) => ({ ...p, _age: p._age + 1 }));
        return [{ ...next, _key: `t-${Date.now()}`, _age: 0 }, ...aged].slice(0, 8);
      });
    }, 3500);
    return () => clearInterval(id);
  }, [paused, alerts]);

  if (!feed.length) return null;

  return (
    <div data-testid="alert-ticker" className="bg-white border border-line rounded-2xl overflow-hidden">
      <div className="px-4 py-2 border-b border-line/60 flex items-center justify-between bg-gradient-to-r from-amber/8 via-white to-white">
        <div className="flex items-center gap-2">
          <div className="relative">
            <span className="absolute -left-0.5 -top-0.5 w-2.5 h-2.5 rounded-full bg-amber/40 animate-ping" />
            <span className="relative w-1.5 h-1.5 rounded-full bg-amber inline-block" />
          </div>
          <span className="text-[12px] font-medium tracking-tight">Live alert stream</span>
          <span className="text-[10px] text-ink-subtle">· Atom Proton v2.4 · {tick} new since you opened this view</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setPaused(!paused)} data-testid="ticker-toggle" className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] border border-line rounded-full hover:bg-bg-hover">
            {paused ? <><Play size={9} strokeWidth={1.5} /> Resume</> : <><Pause size={9} strokeWidth={1.5} /> Pause</>}
          </button>
          <Link to="/alerts" className="text-[10px] text-amber-600 hover:underline">Open queue →</Link>
        </div>
      </div>
      <div className="flex overflow-hidden">
        <div className="flex gap-2 p-2 overflow-x-auto sidebar-scroll">
          {feed.map((a) => (
            <Link
              key={a._key}
              to={`/alerts/${a.id}`}
              className={`shrink-0 w-[280px] border rounded-xl p-2.5 transition-all hover:border-amber/40 hover:shadow-sm ${
                a._age === 0 ? "border-amber/40 bg-amber/5 animate-fade-in" : "border-line bg-white"
              }`}
              style={{ opacity: 1 - a._age * 0.06 }}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium tracking-wide ${
                  a.priority === "P1" ? "bg-risk-veryhigh/10 text-risk-veryhigh"
                  : a.priority === "P2" ? "bg-risk-high/10 text-risk-high"
                  : "bg-risk-medium/10 text-risk-medium"
                }`}>{a.priority}</span>
                {a._age === 0 && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber text-white font-medium">NEW</span>}
                <span className="text-[9px] text-ink-subtle ml-auto">{a._age === 0 ? "now" : `${a._age * 3}s ago`}</span>
              </div>
              <div className="text-[12px] font-medium truncate">{a.subject_name}</div>
              <div className="text-[10px] text-ink-subtle truncate">{a.rule}</div>
              <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-line/50">
                <span className="text-[10px] text-ink-muted">{a.id}</span>
                <span className="text-[10px] tabular font-medium">{formatNGN(a.amount_at_risk, { short: true })}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
